// ===================================================================================================
// InstantQuotesSearchMainForm.jsx
// ---------------------------------------------------------------------------------------------------
// - Top form; fields are driven by the incoming schema Options.
// - Uses PlaceAutocomplete for Origin/Destination with server-side, paginated search.
// - Honors View mode: disables inputs and shows labels instead of raw IDs.
// - Keeps the existing M_* components and visual style.
// - Adds "Service scope" select (Door/Port) that drives PlaceTypeId filters for the autocompletes.
// ---------------------------------------------------------------------------------------------------

import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { MInputTextField, MSelect } from 'src/M_Components';
import PlaceAutocomplete from './PlaceAutocomplete';

// getOpt: read a schema option by Name (defensive on missing arrays/props)
const getOpt = (schema, name) => (schema || []).find(o => o?.Name === name);

//====================================================
// Map service scope to PlaceTypeIds for origin/destination
// 1 = Port, 2 = Location
//====================================================
const serviceToPlaceTypes = (service) => {
  switch (service) {
    case 'D2D': return { origin: 2, destination: 2 }; // Door to Door: both are locations
    case 'D2P': return { origin: 2, destination: 1 }; // Door to Port: origin location, destination port
    case 'P2D': return { origin: 1, destination: 2 }; // Port to Door: origin port, destination location
    case 'P2P':
    default:    return { origin: 1, destination: 1 }; // Port to Port: both are ports
  }
};

// Service scope options; label keys are i18n-friendly
const SERVICE_OPTIONS = [
  { value: 'D2D', labelKey: 'DoorToDoor' },
  { value: 'D2P', labelKey: 'DoorToPort' },
  { value: 'P2D', labelKey: 'PortToDoor' },
  { value: 'P2P', labelKey: 'PortToPort' },
];

//====================================================
// Form component for the header section of Instant Quotes
//====================================================
export default function InstantQuotesSearchMainForm({
  schema,
  formInfo,
  setFormInfo,
  containerTypes,
  packageTypes, // reserved for future use
  isViewMode,
}) {
  const { t } = useTranslation();

  // Resolve which fields exist in the incoming schema
  const originOpt = getOpt(schema, 'OriginPlace');
  const destOpt   = getOpt(schema, 'DestinationPlace');
  const depOpt    = getOpt(schema, 'DepartureDate');
  const contOpt   = getOpt(schema, 'ContainerType');
  const markupOpt = getOpt(schema, 'MarkupPercent');

  // Normalize container types for MSelect
  const containerTypeOptions = (containerTypes || []).map(c => ({ label: c.Name, value: c.Id }));

  // Compute place type filters from the current service scope
  const placeTypes = serviceToPlaceTypes(formInfo?.ServiceScope || 'P2P');

  return (
    <div style={{ display:'flex', flexDirection: 'column',padding: '20px', borderRadius: '8px' }}>
      <span
        style={{
          fontWeight: 'bold',
          color: 'var(--color-red-dark)',
          fontSize: '20px',
          display: 'inline-block',
          marginBottom: '20px',
          borderBottom: '2px solid var(--color-red-dark)',
        }}
      >
        {t('Instant Quotes')}
      </span>

      <div
        style={{
          marginTop: '20px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
        }}
      >
        {/* SERVICE SCOPE (Door/Port) */}
        <MSelect
          label={t('ServiceScope') || 'Service'}
          autocomplete
          required
          // Build localized options at render time
          options={SERVICE_OPTIONS.map(o => ({ value: o.value, label: t(o.labelKey) }))}
          // Derive current value object from formInfo.ServiceScope
          value={(() => {
            const opt = SERVICE_OPTIONS.find(o => o.value === (formInfo?.ServiceScope || 'P2P'));
            return opt ? { value: opt.value, label: t(opt.labelKey) } : null;
          })()}
          onChangeValue={(selected) => {
            // On scope change, reset origin/destination to avoid stale, incompatible IDs
            const next = selected?.value || 'P2P';
            setFormInfo(prev => ({
              ...prev,
              ServiceScope: next,
              OriginPlaceId: null,
              DestinationPlaceId: null,
              OriginPlaceLabel: '',
              DestinationPlaceLabel: '',
            }));
          }}
          disabled={isViewMode}
        />

        {/* ORIGIN */}
        {originOpt && (
          <PlaceAutocomplete
            label={originOpt?.NormalizedName || originOpt?.Name || t('Origin')}
            valueId={formInfo?.OriginPlaceId}           // submit-time value (Id)
            initialLabel={formInfo?.OriginPlaceLabel}   // visual label during View mode or restore
            required={!!originOpt?.IsRequired}
            disabled={isViewMode}
            placeTypeId={placeTypes.origin}             // filtered type by scope
            onChange={(sel) =>
              setFormInfo((prev) => ({
                ...prev,
                OriginPlaceId: sel?.value ?? null,
                OriginPlaceLabel: sel?.label || prev.OriginPlaceLabel || '',
              }))
            }
          />
        )}

        {/* DESTINATION */}
        {destOpt && (
          <PlaceAutocomplete
            label={destOpt?.NormalizedName || destOpt?.Name || t('Destination')}
            valueId={formInfo?.DestinationPlaceId}
            initialLabel={formInfo?.DestinationPlaceLabel}
            required={!!destOpt?.IsRequired}
            disabled={isViewMode}
            placeTypeId={placeTypes.destination}
            onChange={(sel) =>
              setFormInfo((prev) => ({
                ...prev,
                DestinationPlaceId: sel?.value ?? null,
                DestinationPlaceLabel: sel?.label || prev.DestinationPlaceLabel || '',
              }))
            }
          />
        )}

        {/* DEPARTURE DATE */}
        {depOpt && (
          <MInputTextField
            type="date"
            label={depOpt?.NormalizedName || depOpt?.Name || t('Departure Date')}
            value={formInfo?.DepartureDate || ''}
            onChange={(e) => setFormInfo({ ...formInfo, DepartureDate: e.target.value })}
            required={!!depOpt?.IsRequired}
            disabled={isViewMode}
          />
        )}

        {/* CONTAINER TYPE */}
        {contOpt && (
          <MSelect
            label={contOpt?.NormalizedName || contOpt?.Name || t('Container Type')}
            autocomplete
            required
            options={containerTypeOptions}
            value={containerTypeOptions.find((opt) => opt.value === formInfo?.ContainerTypeId) || null}
            onChangeValue={(selected) => setFormInfo({ ...formInfo, ContainerTypeId: selected?.value || null })}
            disabled={isViewMode}
          />
        )}

        {/* MARKUP (raw string; accepts "10" or "10%") */}
        {markupOpt && (
          <MInputTextField
            label={markupOpt?.NormalizedName || markupOpt?.Name || t('Markup Percent')}
            value={formInfo?.MarkupPercent ?? ''}
            onChange={(e) => setFormInfo({ ...formInfo, MarkupPercent: e.target.value })}
            required={!!markupOpt?.IsRequired}
            disabled={isViewMode}
            placeholder="e.g. 10%"
          />
        )}
      </div>
    </div>
  );
}

InstantQuotesSearchMainForm.propTypes = {
  schema: PropTypes.array,
  formInfo: PropTypes.object,
  setFormInfo: PropTypes.func,
  containerTypes: PropTypes.array,
  packageTypes: PropTypes.array,
  isViewMode: PropTypes.bool,
};
